/**
 * Shared closed-loop detection for built chains.
 *
 * A closed loop (oval track, U-turn pair) returns to the origin pose after
 * one full revolution: end-pose translation ≈ 0 and accumulated yaw ≈ a
 * multiple of 360°. Both the mover collision (wrap-around seam on the same
 * chain) and the module collision (excluding the first/last module seam)
 * need the identical predicate — keeping it in one place avoids the two
 * tolerances silently drifting apart.
 */

import type { BuiltChain } from './ChainBuilder.js';

export const CLOSED_LOOP_TOLERANCE_MM = 0.1;
export const CLOSED_LOOP_TOLERANCE_DEG = 0.1;

/**
 * True when `chain` forms a closed loop — its end pose coincides with the
 * identity start pose within sub-mm / sub-tenth-degree tolerance.
 */
export function isChainClosed(chain: BuiltChain): boolean {
  const [ex, ey] = chain.endPose.positionMm;
  if (
    Math.abs(ex) > CLOSED_LOOP_TOLERANCE_MM ||
    Math.abs(ey) > CLOSED_LOOP_TOLERANCE_MM
  ) {
    return false;
  }
  const yawMod360 = ((chain.endPose.yawDeg % 360) + 360) % 360;
  const yawDelta = Math.min(yawMod360, 360 - yawMod360);
  return yawDelta < CLOSED_LOOP_TOLERANCE_DEG;
}
